import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, BarChart3, Truck, CheckCircle, Package,
  TrendingUp, Clock, RefreshCw, Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import useClienteActivo from '@/hooks/useClienteActivo';
import ClienteActivoSelector from '@/components/ClienteActivoSelector';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';

const ClientePrendasBalance = () => {
  const navigate = useNavigate();
  const {
    clientesAsignados,
    clienteActivoId,
    setClienteActivo,
  } = useClienteActivo();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    totalRecolecciones: 0,
    totalEntregadas: 0,
    enProceso: 0,
    totalPrendasRecogidas: 0,
    totalPrendasEntregadas: 0,
    ultimasRecolecciones: [],
    ultimasEntregas: [],
  });

  const sinClientes = clientesAsignados.length === 0;

  const loadBalance = useCallback(async () => {
    if (!clienteActivoId) return;
    setIsLoading(true);
    try {
      const [resRec, resEnt, resEnC] = await Promise.all([
        fetchEntregasClientes({
          query: { 'cliente.id': clienteActivoId, tipo: 'Recoleccion' },
          limit: 5, page: 1,
        }),
        fetchEntregasClientes({
          query: { 'cliente.id': clienteActivoId, tipo: 'Entrega', estado: 'Entregado' },
          limit: 5, page: 1,
        }),
        fetchEntregasClientes({
          query: { 'cliente.id': clienteActivoId, tipo: 'Entrega', estado: { $in: ['Alistado', 'En Camino'] } },
          limit: 100, page: 1,
        }),
      ]);

      const prendasRecogidas = (resRec.data || []).reduce((s, r) =>
        s + (r.items || []).reduce((ss, i) => ss + (i.cantidad_alistada || 0), 0), 0);

      const prendasEntregadas = (resEnt.data || []).reduce((s, e) =>
        s + (e.items || []).reduce((ss, i) => ss + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0), 0);

      setData({
        totalRecolecciones: resRec.totalRecords || 0,
        totalEntregadas: resEnt.totalRecords || 0,
        enProceso: resEnC.totalRecords || 0,
        totalPrendasRecogidas: prendasRecogidas,
        totalPrendasEntregadas: prendasEntregadas,
        ultimasRecolecciones: resRec.data || [],
        ultimasEntregas: resEnt.data || [],
      });
    } catch {
      // silently ignore
    } finally {
      setIsLoading(false);
    }
  }, [clienteActivoId]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const StatCard = ({ icon: Icon, label, value, color, subLabel }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`${color} p-2.5 rounded-lg shrink-0`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 truncate">{label}</p>
            {subLabel && <p className="text-xs text-gray-400">{subLabel}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mis-prendas')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="h-5 w-5 text-blue-600 shrink-0" />
            <h1 className="text-base font-semibold text-gray-900 truncate">Balance</h1>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ClienteActivoSelector
              clientes={clientesAsignados}
              activoId={clienteActivoId}
              onSelect={setClienteActivo}
              variant="inline"
            />
            <Button
              variant="ghost" size="icon"
              onClick={loadBalance}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {sinClientes ? (
        <div className="p-3">
          <Card>
            <CardContent className="p-10 text-center">
              <Building2 className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No tienes clientes asignados</p>
              <p className="text-sm text-gray-500 mt-1">Pide al administrador que te asigne uno o varios clientes.</p>
            </CardContent>
          </Card>
        </div>
      ) : isLoading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="text-sm text-gray-500 mt-3">Calculando balance...</p>
        </div>
      ) : (
        <div className="p-3 space-y-4">
          {/* Stats principales */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Resumen general</p>
            <div className="grid grid-cols-2 gap-2">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
                <StatCard icon={Truck} label="Recolecciones" value={data.totalRecolecciones} color="bg-purple-500" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                <StatCard icon={CheckCircle} label="Entregas completadas" value={data.totalEntregadas} color="bg-green-500" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <StatCard icon={Clock} label="En proceso" value={data.enProceso} color="bg-orange-500" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <StatCard
                  icon={TrendingUp}
                  label="Efectividad"
                  value={data.totalRecolecciones > 0
                    ? `${Math.round((data.totalEntregadas / data.totalRecolecciones) * 100)}%`
                    : '-'}
                  color="bg-blue-500"
                />
              </motion.div>
            </div>
          </div>

          {/* Prendas */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">Prendas (últimas 5)</p>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                      <Truck className="h-4 w-4" />
                      <span className="text-xs">Recogidas</span>
                    </div>
                    <p className="text-3xl font-bold text-purple-700">{data.totalPrendasRecogidas}</p>
                  </div>
                  <div className="w-px h-12 bg-gray-200" />
                  <div className="text-center flex-1">
                    <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                      <Package className="h-4 w-4" />
                      <span className="text-xs">Entregadas</span>
                    </div>
                    <p className="text-3xl font-bold text-green-700">{data.totalPrendasEntregadas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Últimas recolecciones */}
          {data.ultimasRecolecciones.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Últimas recolecciones</p>
                <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => navigate('/mis-prendas/recolecciones')}>
                  Ver todas
                </Button>
              </div>
              <Card>
                <CardContent className="p-0 divide-y">
                  {data.ultimasRecolecciones.map((r) => {
                    const total = (r.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
                    return (
                      <div key={r._id} className="flex items-center justify-between p-3">
                        <div>
                          <p className="text-sm font-medium text-purple-700">{r.consecutivo}</p>
                          <p className="text-xs text-gray-400">{formatDate(r.fecha_recoleccion || r.fecha_creacion)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-700">{total} prendas</p>
                          <Badge className={`text-xs ${r.estado === 'Verificado' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {r.estado}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Últimas entregas */}
          {data.ultimasEntregas.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Últimas entregas</p>
                <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => navigate('/mis-prendas/entregas')}>
                  Ver todas
                </Button>
              </div>
              <Card>
                <CardContent className="p-0 divide-y">
                  {data.ultimasEntregas.map((e) => {
                    const total = (e.items || []).reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0);
                    return (
                      <div key={e._id} className="flex items-center justify-between p-3">
                        <div>
                          <p className="text-sm font-medium text-green-700">{e.consecutivo}</p>
                          <p className="text-xs text-gray-400">{formatDate(e.fecha_entrega)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-700">{total} prendas</p>
                          <Badge className="text-xs bg-green-100 text-green-800">Entregado</Badge>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {!clienteActivoId && !sinClientes && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <p className="text-sm text-orange-700">
                  Tu cuenta no tiene un cliente activo. Contacta al administrador.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientePrendasBalance;
